import type { BusinessType } from '@/types/tenant';

// ─── Design tokens ──────────────────────────────────────────────────────────
const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

interface BusinessTypeSelectorProps {
  value?: BusinessType;
  onChange: (type: BusinessType) => void;
  disabled?: boolean;
}

const OPTIONS: { type: BusinessType; title: string; desc: string; icon: string }[] = [
  {
    type: 'RETAIL',
    title: 'Retail',
    desc: 'Unit-based inventory, barcode scanning, variant matrix',
    icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
  },
  {
    type: 'RESTAURANT',
    title: 'Restaurant',
    desc: 'Recipe-based inventory, KDS, table management',
    icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z',
  },
];

export function BusinessTypeSelector({ value, onChange, disabled }: BusinessTypeSelectorProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {OPTIONS.map((opt) => {
        const isSelected = value === opt.type;
        return (
          <button
            key={opt.type}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.type)}
            className={cn(
              'relative flex flex-col items-start gap-2 p-4 text-left transition-all duration-200',
              GLASS_PANEL,
              isSelected && 'ring-2 ring-brand-500 shadow-[0_8px_20px_-12px_rgba(59,130,246,0.5)]',
              !isSelected && 'hover:shadow-lg',
              disabled && 'opacity-50 cursor-not-allowed',
              !disabled && 'cursor-pointer',
            )}
          >
            {isSelected && (
              <span className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-white">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
            <div className={cn(
              'flex h-10 w-10 items-center justify-center rounded-xl',
              isSelected
                ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400'
                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500',
            )}>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={opt.icon} />
              </svg>
            </div>
            <div>
              <p className={cn(
                'text-sm font-semibold',
                isSelected ? 'text-brand-700 dark:text-brand-300' : 'text-neutral-700 dark:text-neutral-200',
              )}>
                {opt.title}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{opt.desc}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
